import { NgClass } from "@angular/common";
import { AfterViewInit, Component, ElementRef, HostListener, Input, ViewChild } from "@angular/core";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { CollectedPreviewItem, GameType, ItemSearch, SearchRequest, SearchResponse, UpdateDeckRequest } from "@apiserver-rest/ponder-data-client-base";
import { debounceTime, distinctUntilChanged, fromEvent, map, Subscription, take } from "rxjs";
import { DeckBaseComponent } from "../deck-base.component";
import { isMobileScreen } from "@ponder-shared/utils/screenBreakpoints";

@Component({
    selector: "deck-card-search",
    imports: [NgClass],
    templateUrl: "./deck-card-search.component.html"
})
export class DeckCardSearchComponent implements AfterViewInit {
    @Input({ required: true }) game!: GameType;
    @Input({ required: true }) deckComponent!: DeckBaseComponent;

    @ViewChild("deckCardSearch") deckCardSearchRef!: ElementRef<HTMLInputElement>;
    
    public shouldShow: boolean = false;
    public searchResults?: CollectedPreviewItem[];
    private searchSub!: Subscription;
    private isSearching: boolean = false;
    public hasExactMatch: boolean = false;

    public constructor(private ponderDataClient: PonderDataClient, private elementRef: ElementRef) {

    }

    public ngAfterViewInit(): void {
        this.searchSub = fromEvent(this.deckCardSearchRef.nativeElement, "input")
            .pipe(map((event: Event) => (event.target as HTMLInputElement).value))
            .pipe(debounceTime(750))        // keep same as primary search debounce for consistency
            .pipe(distinctUntilChanged())
            .subscribe({
                next: (value: string) => {
                    this.onInputChange(value);
                }
            });
    }

    public ngOnDestroy(): void {
        this.searchSub.unsubscribe();
    }

    private reset(): void {
        this.shouldShow = false;
        this.searchResults = undefined;
        this.deckCardSearchRef.nativeElement.value = "";
    }

    private onClose(): void {
        this.shouldShow = false;
    }

    // -------------------------------------------------------------------------------------
    // Global Events
    // -------------------------------------------------------------------------------------

    private mouseDownOutside: boolean = false;

    @HostListener("document:keydown.escape", ["$event"]) 
    private onEscapePressed(event: KeyboardEvent): void {
        this.onClose();
    }

    @HostListener("document:mousedown", ["$event"])
    private onGlobalMouseDown(event: MouseEvent): void {
        if (this.searchResults && !this.elementRef.nativeElement.contains(event.target as HTMLInputElement)) {
            this.mouseDownOutside = true;
        }
    }

    @HostListener("document:mouseup", ["$event"])
    private onGlobalMouseUp(event: MouseEvent): void {
        // Did the user click outside of our modal panel?
        if (this.searchResults && this.mouseDownOutside && !this.elementRef.nativeElement.contains((event.target as HTMLInputElement))) {
            this.onClose();
        }

        this.mouseDownOutside = false;
    }

    // -------------------------------------------------------------------------------------
    // Actions
    // -------------------------------------------------------------------------------------

    public onInputFocus(): void {
        if (this.searchResults) {
            this.shouldShow = true;
        }
    }

    private onInputChange(value: string): void {
        if (this.isSearching) {
            // todo probably need to cancel current search and start a new one
            // we have already restricted time between searches with the debounce on the event
            // this is just hiding real searches
            // todo also the same to the main and user search
            return;
        }

        if (value.length < 3) {
            return;
        }

        this.isSearching = true;
        this.searchResults = undefined;

        var searchRequest = new SearchRequest({
            game: this.game,
            item: new ItemSearch({
                name: value,
                exact: false,
                distinct: true,
                useSetPriority: true,
                sortAlphabetical: true,
                sortCollectedCount: true
            })
        });

        this.ponderDataClient
            .search(searchRequest)
            .pipe(take(1))
            .subscribe({
                next: (response: SearchResponse) => {
                    if (response.success && response.results) {
                        this.searchResults = response.results;
                        this.hasExactMatch = response.isExact ?? false;
                    }
                }
            })
            .add(() => {
                this.isSearching = false;
                this.shouldShow = (this.searchResults ? true : false);
            });
    }

    public onResultClicked(result: CollectedPreviewItem): void {
        this.reset();

        if (result.item.name && result.item.id) {
            var specialDelta = result.collected?.specialCount ? 1 : 0;      // Add foil version if we have one
            var normalDelta = (specialDelta > 0 ? 0 : 1);

            if (result.item.data?.magic) {
                if (!result.item.data.magic.treatment.normal && result.item.data.magic.treatment.special) {
                    // Foil only
                    specialDelta = 1;
                    normalDelta = 0;
                }
            }

            this.deckComponent.addCardToActiveLayer(result.item.name, result.item.id, true, normalDelta, specialDelta);
        }
    }

    public isMobileScreen(): boolean {
        return isMobileScreen();
    }
}